// @ts-check

import { api } from '@/api'
import { useMemoize } from '@vueuse/core'

/** @returns {Promise<InvoiceIncomeResource>} */
export const createInvoice = async () => {
  const res = await api.post('incomes/invoices')
  return res.data
}

/**
 * @param {InvoiceIncomeResource['id']} incomeId
 * @returns {Promise<{ items: InvoiceItems, totals: InvoiceTotals }>}
 */
export const getInvoiceItems = async (incomeId) => {
  /** @type {import('axios').AxiosResponse<InvoiceItemResource[]> & { invoice_totals: InvoiceTotals }} */
  const res = await api.get(`incomes/${incomeId}/invoices/items`)
  return {
    items: res.data,
    totals: res.invoice_totals
  }
}

/**
 * @param {InvoiceIncomeResource['id']} incomeId
 * @param {unknown} payload
 * @returns {Promise<InvoiceIncomeResource>}
 */
export const updateInvoice = async (incomeId, payload) => {
  const res = await api.patch(`incomes/${incomeId}/invoices`, payload)
  return res.data
}

/**
 * @param {InvoiceIncomeResource['id']} incomeId
 * @returns {Promise<FileResource>}
 */
export const getInvoicePreview = async (incomeId) => {
  /** @type {import('axios').AxiosResponse & FileResource} */
  const res = await api.get(`incomes/${incomeId}/preview`)
  return res
}

/** @returns {Promise<InvoiceNumberResource['invoice_number']>} */
export const getNextInvoiceNumber = async () => {
  /** @type {import('axios').AxiosResponse & InvoiceNumberResource} */
  const res = await api.get('invoices/numbers/next')
  return res.invoice_number
}

/** @returns {Promise<boolean>} */
export const getHasPreviousBookkeepingRecords = async () => {
  const res = await api.get(`company/bookkeeping/materials`)
  return res.data.has_previous
}

/** @type {(incomeId: InvoiceIncomeResource['id']) => Promise<void>} */
export const sendInvoice = async (incomeId) =>
  api.post(`incomes/${incomeId}/invoices/send`)

/** @returns {Promise<PaymentTerms>} */
export const getPaymentTerms = async () => {
  const { data: paymentTerms } = await api.get('payment-terms')
  return paymentTerms
}

/** @type {import('@vueuse/core').UseMemoizeReturn<Promise<InvoiceDeliveryMethods>, [InvoiceDeliveryMethodParams]>}} */
export const getInvoiceDeliveryMethods = useMemoize(async (params = {}) => {
  const { data: invoiceDeliveryMethods } = await api.get(
    'invoices/delivery-methods',
    { params }
  )
  return invoiceDeliveryMethods
})

/** @type {(invoiceId: InvoiceResource['id']) => Promise<InvoiceIncomeResource>} */
export const makeCreditNote = async (invoiceId) => {
  const res = await api.post(`invoices/${invoiceId}/compensate`)
  return res.data
}

/** @type {(id: InvoiceIncomeResource['id']) => Promise<import('axios').AxiosResponse & { file: string, invoice_number: InvoiceResource['invoice_number'], mime_type: string }>} */
export const downloadInvoice = async (incomeId) =>
  api.get(`incomes/${incomeId}/download`)

/** @type {(id: InvoiceIncomeResource['id'], financingConsent: boolean) => Promise<import('axios').AxiosResponse & { financing_url: string }>} */
export const invoicingApplication = async (id, financingConsent) =>
  api.post(`incomes/${id}/invoices/invoicing-application`, {
    financing_consent: financingConsent
  })

/**
 * @param {InvoiceResource['id']} invoiceId
 * @param {LocalizedPayload|undefined} params
 * @returns {Promise<RestrictionStatusResource>}
 */
export const getInvoiceCanSend = async (invoiceId, params) => {
  const res = await api.get(`invoices/${invoiceId}/can-send`, { params })
  return res.data
}

/** @type {() => Promise<import('axios').AxiosResponse & { is_approved_for_financing: boolean }>} */
export const isApprovedForFinancing = async () =>
  api.get(`invoices/is-approved-for-financing`)

/** @type {import('@vueuse/core').UseMemoizeReturn<Promise<VatDescriptions>, [VatDescriptionQueryParams]>}} */
export const getVatDescriptions = useMemoize(async (params = {}) => {
  const { data: vatDescriptions } = await api.get(
    'vats/zero-vat-descriptions',
    { params }
  )
  return vatDescriptions
})

/** @type {(companyId: number) => Promise<CompanyInvoices>} */
export const getCompanyInvoices = async (companyId) => {
  const { data: companyInvoices } = await api.get(
    `company/${companyId}/invoices`
  )
  return companyInvoices
}

/** @type {(incomeId: InvoiceIncomeResource['id'], payload: PaymentReminderPayload) => Promise<void>} */
export const sendInvoicePaymentReminder = async (incomeId, payload) =>
  api.put(`/incomes/${incomeId}/invoices/payment-reminder`, payload)

/**
 * @param {InvoiceIncomeResource['id']} incomeId
 * @param {InvoiceItemPayload} payload
 * @returns {Promise<{ item: InvoiceItemResource, totals: InvoiceTotals }>}
 */
export const createInvoiceItem = async (incomeId, payload) => {
  /** @type {import('axios').AxiosResponse<InvoiceItemResource> & { invoice_totals: InvoiceTotals }} */
  const res = await api.post(`incomes/${incomeId}/invoices/items`, payload)

  return {
    item: res.data,
    totals: res.invoice_totals
  }
}

/**
 * @param {InvoiceIncomeResource['id']} incomeId
 * @param {InvoiceItemResource['id']} itemId
 * @param {InvoiceItemPayload} payload
 * @returns {Promise<{ item: InvoiceItemResource, totals: InvoiceTotals }>}
 */
export const updateInvoiceItem = async (incomeId, itemId, payload) => {
  /** @type {import('axios').AxiosResponse<InvoiceItemResource> & { invoice_totals: InvoiceTotals }} */
  const res = await api.patch(
    `incomes/${incomeId}/invoices/items/${itemId}`,
    payload
  )

  return {
    item: res.data,
    totals: res.invoice_totals
  }
}
